import type {
  FormattedGameMode,
  GameModeData,
  GameModeRequestData,
  GameModesDashResponse,
  GameModesResponse,
  GameModesResponseData
} from '@library/types/gameModes';
import type { AllowedStatesDataShort } from '@library/types/allowedStates';
import type { ComboboxOption } from '@library/components/selects';
import type { GameTypeData } from '@library/types/gameTypes';
import type { LeagueProjectionAttributes } from '@library/types/leagues';
import type { SelectOptions } from '@library/types/utils';
import { formatDate } from '@library/component-utils/dateTime';
import { formatLabelByKey } from '@bo-utils/common';
import { isLeagueFullDuration } from '@bo-utils/leagueProjectionFilters';

export const transformGameModes = (
  response: GameModesResponse | undefined
): Array<FormattedGameMode> => {
  if (!response) {
    return [];
  }

  return response.data?.reduce<{ id: number; label: string; name: string }[]>(
    (gameModes, { attributes, id }) => {
      if (attributes.name && attributes.name !== '') {
        gameModes.push({
          id: Number(id),
          label: formatLabelByKey(attributes.name, ['f2p', 'ftp']),
          name: attributes.name
        });
      }
      return gameModes;
    },
    []
  );
};

export const getGameModeSelectOptions = (
  gameModes: Array<FormattedGameMode> | undefined
): SelectOptions => {
  if (!gameModes) {
    return [];
  }

  return gameModes.map(({ id, name }) => ({
    key: String(id),
    name: formatLabelByKey(name, ['f2p', 'ftp']),
    value: String(id)
  }));
};

export const getGameModeComboboxOptions = (
  gameModes: Array<FormattedGameMode> | undefined,
  isFullDuration?: boolean
): Array<ComboboxOption> => {
  if (!gameModes) {
    return [];
  }

  return gameModes.map(({ id, name }) => ({
    label: formatLabelByKey(name, ['f2p', 'ftp']),
    value: id,
    // Squads game mode is disabled for sub-durations
    isDisabled: !isFullDuration && name.includes('squads')
  }));
};

export const getFilteredGameModesByDuration = (
  gameModes: Array<FormattedGameMode>,
  durations: Array<LeagueProjectionAttributes>,
  selectedDurationId: string
): Array<FormattedGameMode> => {
  const durationName = durations.find(
    ({ leagueProjectionFilterId }) =>
      leagueProjectionFilterId === selectedDurationId
  )?.leagueProjectionFilterName;

  const isFullDuration = isLeagueFullDuration(durationName);

  return gameModes.filter(
    ({ name }) => isFullDuration || !name.includes('squads')
  );
};

export const mapGameTypeRelationshipToGameMode = (
  gameType?: GameTypeData,
  hasNext?: boolean
): string => {
  if (!gameType) {
    return '';
  }
  return `${gameType.name} #${gameType.id}${hasNext ? ', ' : ''}`;
};

export const transformGameModeData = (
  response: GameModesDashResponse | undefined,
  relationships: {
    gameTypes: Array<GameTypeData>;
    allowedStates: Array<AllowedStatesDataShort>;
  }
): Array<GameModeData> => {
  return response?.data?.map((gameMode: GameModesResponseData) => {
    return {
      id: gameMode.id,
      name: gameMode.attributes.name,
      createdAt: formatDate({ date: gameMode.attributes.created_at }),
      updatedAt: formatDate({ date: gameMode.attributes.updated_at }),
      allowedStates: gameMode.relationships?.allowed_states?.data?.map(
        (allowedState) => {
          return relationships.allowedStates?.find(
            ({ id }) => id === allowedState.id
          )?.id;
        }
      ),
      gameTypes: gameMode.relationships?.game_types?.data?.length
        ? gameMode.relationships.game_types.data?.map((gameType) => {
            return relationships.gameTypes?.find(({ id }) => id === gameType.id)
              ?.id;
          })
        : []
    };
  }) as Array<GameModeData>;
};

export const transformGameModeDataShort = (
  response: GameModesDashResponse | undefined
): Array<GameModeData> => {
  return response?.data?.map((gameMode: GameModesResponseData) => {
    return { id: gameMode.id, name: gameMode.attributes.name };
  }) as Array<GameModeData>;
};

export const mapGameModeDataToRequest = (
  gameModeFormData: Partial<GameModeData>
): GameModeRequestData => {
  const { name, allowedStates, gameTypes } = gameModeFormData;

  return {
    name: name || '',
    game_type_ids: gameTypes || [],
    allowed_state_ids: allowedStates || []
  };
};

export const getGameModeLinks = (ids: Array<string>) => {
  return ids?.map((item) => {
    const itemStr = String(item);
    const parts = itemStr.split('#');
    const id = parts.length > 1 ? parts[1] : itemStr;

    return {
      id: item,
      link: `/game-types?id=${id}`
    };
  });
};

export const getAllowedStatesLinks = (ids: Array<string>) => {
  return ids?.map((item) => {
    const itemStr = String(item);
    const parts = itemStr.split('#');
    const id = parts.length > 1 ? parts[1] : itemStr;

    return {
      id: item,
      link: `/allowed-states?id=${id}`
    };
  });
};

export const renameRelationshipsForTable = (
  gameMode: GameModeData,
  relationships: {
    gameTypes: Array<GameTypeData>;
    allowedStates: Array<AllowedStatesDataShort>;
  }
) => {
  return {
    ...gameMode,
    gameTypes: gameMode.gameTypes.map((gameType, index) => {
      return mapGameTypeRelationshipToGameMode(
        relationships?.gameTypes?.find(({ id }) => id === gameType),
        index !== (gameMode.gameTypes.length ?? 0) - 1
      );
    }),
    allowedStates: gameMode?.allowedStates?.map((allowedState, index) => {
      const foundState = relationships?.allowedStates?.find(
        ({ id }) => id === allowedState
      );
      return `${foundState?.name} #${foundState?.id}${
        index !== (gameMode.allowedStates.length ?? 0) - 1 ? ', ' : ''
      }`;
    })
  };
};
